import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { createNarrativeTemplate, readNarrativeTemplates } from '@api/narratives';
import {
  Announcement,
  Button,
  ButtonV2,
  Input,
  ModalV2 as Modal,
  OverlayTriggerTooltip,
  Radio,
  Select,
  StackPlusIcon,
  TextArea,
  toast,
  truncateTextWithEllipsis,
} from '@utilities';

import { DESCRIPTION_MAX_LENGTH, OPTIONS, TEMPLATE_NAME_MAX_LENGTH } from './utilities/constants';

import styles from './_index.module.scss';

const SaveAsTemplate = ({
  canAccessChat,
  isPublicShare,
  onTemplateSaved,
  topicIds = [],
  triggerClassName = '',
  triggerVariant = 'button',
}) => {
  const [error, setError] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedOption, setSelectedOption] = useState(OPTIONS.CREATE);
  const [templateName, setTemplateName] = useState('');
  const [description, setDescription] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [hasLoadedTemplates, setHasLoadedTemplates] = useState(false);
  const radioName = 'save-as-template-option';

  const isCreateSelected = selectedOption === OPTIONS.CREATE;
  const isUpdateSelected = selectedOption === OPTIONS.UPDATE;
  const canRender = canAccessChat && !isPublicShare;
  const hasTopicIds = Array.isArray(topicIds) && topicIds.length > 0;
  const isTemplateNameMaxLength = templateName.length >= TEMPLATE_NAME_MAX_LENGTH;
  const isDescriptionMaxLength = description.length >= DESCRIPTION_MAX_LENGTH;

  const templateOptions = templates.map((template) => ({
    description: template?.description || '',
    label: truncateTextWithEllipsis({ text: template?.name || 'Untitled Template' }),
    value: template?.id,
  }));
  const getErrorMessage = (detail, fallbackMessage) => {
    if (typeof detail === 'string') return detail;
    return detail?.[0]?.msg || fallbackMessage;
  };

  useEffect(() => {
    if (isCreateSelected) {
      setSelectedTemplate(null);
      return;
    }

    if (!selectedTemplate) {
      setDescription('');
    }
  }, [isCreateSelected, selectedTemplate]);

  const getTemplates = async () => {
    try {
      setIsLoading(true);
      setError('');

      const response = await readNarrativeTemplates();

      setTemplates(Array.isArray(response) ? response : []);
      setHasLoadedTemplates(true);
    } catch (requestError) {
      const detail = requestError?.response?.data?.detail;
      setError(getErrorMessage(detail, 'Unable to load templates.'));
      console.error(requestError);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isActive || !isUpdateSelected || hasLoadedTemplates) return;
    getTemplates();
  }, [hasLoadedTemplates, isActive, isUpdateSelected]);

  useEffect(() => {
    if (isActive) return;

    setError('');
    setIsLoading(false);
    setIsSubmitting(false);
    setSelectedOption(OPTIONS.CREATE);
    setTemplateName('');
    setDescription('');
    setTemplates([]);
    setSelectedTemplate(null);
    setHasLoadedTemplates(false);
  }, [isActive]);

  if (!canRender) return null;

  const handleTemplateChange = (templateOption) => {
    setSelectedTemplate(templateOption);
    setDescription(templateOption?.description || '');
  };

  const handleSelectOption = (nextOption) => {
    setSelectedOption(nextOption);
    setError('');
    setDescription('');
    setSelectedTemplate(null);
  };

  const handleSubmit = async () => {
    setError('');

    if (isUpdateSelected) {
      toast('Update template API is not available yet.');
      return;
    }

    if (!hasTopicIds) {
      setError('Template can be saved after topic cards finish loading.');
      return;
    }

    try {
      setIsSubmitting(true);

      await createNarrativeTemplate({
        description: description?.trim() || '',
        name: templateName.trim(),
        topicIds,
      });

      toast.success(`Template "${templateName.trim()}" saved successfully.`);
      onTemplateSaved?.({
        description: description?.trim() || '',
        id: `temp-template-${Date.now()}`,
        name: templateName.trim(),
        topicIds: [...topicIds],
      });
      setIsActive(false);
    } catch (requestError) {
      const detail = requestError?.response?.data?.detail;
      setError(getErrorMessage(detail, 'Unable to save template.'));
      console.error(requestError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled =
    (isCreateSelected
      ? templateName.trim().length === 0 || !hasTopicIds || isTemplateNameMaxLength
      : !selectedTemplate) ||
    isDescriptionMaxLength ||
    isLoading ||
    isSubmitting;

  return (
    <>
      {triggerVariant === 'icon' ? (
        <OverlayTriggerTooltip aria-label="Save as Template" content="Save as Template">
          <button
            className={triggerClassName}
            data-testid="save-as-template-icon"
            onClick={() => setIsActive(true)}
            type="button"
          >
            <StackPlusIcon />
          </button>
        </OverlayTriggerTooltip>
      ) : (
        <ButtonV2
          data-testid="save-as-template-button"
          onClick={() => setIsActive(true)}
          shape="round"
        >
          Save as Template
        </ButtonV2>
      )}

      <Modal
        buttons={[
          <Button
            data-testid="save-as-template-cancel-button"
            key="cancel-btn"
            onClick={() => setIsActive(false)}
            text="Cancel"
            variant="secondary"
          />,
          <Button
            data-testid="save-as-template-save-button"
            isDisabled={isSubmitDisabled}
            isLoading={isSubmitting}
            key="submit-btn"
            onClick={handleSubmit}
            text="Save"
          />,
        ]}
        className={styles['save-as-template']}
        data-testid="save-as-template-modal"
        isActive={isActive}
        onClose={() => setIsActive(false)}
        title="Save as Template"
      >
        {error ? (
          <Announcement data-testid="save-as-template-error" text={error} variant="error" />
        ) : null}
        <p data-testid="save-as-template-subtext">
          Create a new template or update an existing one
        </p>
        <ul>
          <li data-testid="save-as-template-option-create">
            <Radio
              isChecked={isCreateSelected}
              label="Create new template"
              name={radioName}
              onChange={handleSelectOption}
              value={OPTIONS.CREATE}
            />
            {isCreateSelected ? (
              <div className={styles['save-as-template-form-fields']}>
                <div className={styles['save-as-template-field']}>
                  <Input
                    className={styles['save-as-template-field-control']}
                    data-testid="save-as-template-input-name"
                    error={
                      isTemplateNameMaxLength
                        ? 'Maximum number of characters reached. Try something shorter.'
                        : ''
                    }
                    isRequired
                    label="Template Name"
                    maxLength={TEMPLATE_NAME_MAX_LENGTH}
                    onChange={setTemplateName}
                    placeholder="Template name"
                    value={templateName}
                  />
                </div>
                <div className={styles['save-as-template-field']}>
                  <TextArea
                    autoGrow
                    className={styles['save-as-template-field-control']}
                    data-testid="save-as-template-input-description"
                    error={
                      isDescriptionMaxLength
                        ? 'Maximum number of characters reached. Try something shorter.'
                        : ''
                    }
                    label="Description"
                    maxLength={DESCRIPTION_MAX_LENGTH}
                    onChange={setDescription}
                    placeholder="Template description"
                    rows={4}
                    value={description}
                  />
                </div>
              </div>
            ) : null}
          </li>
          <li data-testid="save-as-template-option-update">
            <Radio
              isChecked={isUpdateSelected}
              label="Update existing template"
              name={radioName}
              onChange={handleSelectOption}
              value={OPTIONS.UPDATE}
            />
            {isUpdateSelected ? (
              <div className={styles['save-as-template-form-fields']}>
                <div className={styles['save-as-template-field']}>
                  <Select
                    className={styles['save-as-template-field-control']}
                    data-testid="save-as-template-select-name"
                    isLoading={isLoading}
                    label="Template Name"
                    onChange={handleTemplateChange}
                    options={templateOptions}
                    placeholder="Select template"
                    value={selectedTemplate}
                  />
                </div>
                <div className={styles['save-as-template-field']}>
                  <TextArea
                    autoGrow
                    className={styles['save-as-template-field-control']}
                    data-testid="save-as-template-update-description"
                    error={
                      isDescriptionMaxLength
                        ? 'Maximum number of characters reached. Try something shorter.'
                        : ''
                    }
                    isDisabled={!selectedTemplate}
                    label="Description"
                    maxLength={DESCRIPTION_MAX_LENGTH}
                    onChange={setDescription}
                    placeholder="Template description"
                    rows={4}
                    value={description}
                  />
                </div>
              </div>
            ) : null}
          </li>
        </ul>
      </Modal>
    </>
  );
};

SaveAsTemplate.propTypes = {
  canAccessChat: PropTypes.bool,
  isPublicShare: PropTypes.bool,
  onTemplateSaved: PropTypes.func,
  topicIds: PropTypes.arrayOf(PropTypes.string),
  triggerClassName: PropTypes.string,
  triggerVariant: PropTypes.oneOf(['button', 'icon']),
};

export default SaveAsTemplate;
